import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Hls from 'hls.js';
// Store
import { useSelector, useDispatch } from 'react-redux';
import { getVideoInfo } from '../../features/video/videoSlice';
// Components
import { ChatBox, TranscriptContainer, CommitteeSummary } from '../../components/videos';
import MainLoader from '../../components/Structures/MainLoader';
import TabsSwitch from '../../components/Structures/TabsSwitch';
// Icons
import { IoIosArrowForward, IoIosArrowBack } from 'react-icons/io';

const Video = () => {
  const { videoId, state } = useParams();
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  const hlsRef = useRef(null); // Reference for hls.js instance
  const [showOption, setShowOption] = useState('transcript');
  const [isExpanded, setIsExpanded] = useState(false);

  const { video, isLoading } = useSelector((state) => state.video);

  useEffect(() => {
    if (videoId) {
      dispatch(getVideoInfo({ videoId, state }));
    }
  }, [videoId, dispatch]);

  useEffect(() => {
    if (video.Link) {
      const isHlsStream = video.Link.includes('.m3u8');
      const isMp4 = video.Link.includes('.mp4');

      if (isHlsStream && Hls.isSupported()) {
        hlsRef.current = new Hls();
        hlsRef.current.loadSource(video.Link);
        hlsRef.current.attachMedia(videoRef.current);
      } else if (isMp4 || videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        videoRef.current.src = video.Link;
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, [video]);

  const onGoToTimeStamp = (timeInSeconds) => {
    videoRef.current.currentTime = timeInSeconds;
    videoRef.current.play();
  };

  if (isLoading) return <MainLoader />;

  return (
    <div className={`w-full ${isExpanded ? 'flex flex-col gap-5' : 'flex gap-5'}`}>
      {video && !isLoading && (
        <>
          {/* Video Section */}
          <div className={`${isExpanded ? 'w-full' : 'w-[40%]'} h-full overflow-hidden`}>
            <div className="py-[15px] px-5 border border-[#E5E7EB] bg-[#F9FAFB] mb-4 relative group">
              <div className="border-b border-[#E5E7EB] mb-5 pb-2.5">
                <span className="font-playfair text-[28px] capitalize text-darkblue font-medium leading-8">
                  Recording: <strong>{video.Committee}</strong>
                </span>
              </div>
              <div className="w-full flex justify-center">
                <video className="w-full" ref={videoRef} title="video" controls />
              </div>

              {/* Hover-only expand/collapse button */}
              {!isExpanded ? (
                <button
                  className="opacity-0 group-hover:opacity-100 transition-opacity absolute top-1/2 -translate-y-1/2 right-3 bg-white border border-gray-300 rounded-full p-2 shadow"
                  title="Expand video"
                  onClick={() => setIsExpanded(true)}
                >
                  <IoIosArrowForward className="w-6 h-6" />
                </button>
              ) : (
                <button
                  className="opacity-0 group-hover:opacity-100 transition-opacity absolute top-1/2 -translate-y-1/2 right-3 bg-white border border-gray-300 rounded-full p-2 shadow"
                  title="Collapse video"
                  onClick={() => setIsExpanded(false)}
                >
                  <IoIosArrowBack className="w-6 h-6" />
                </button>
              )}
            </div>

            {/* In non-expanded mode, keep Ask AI below the video */}
            {!isExpanded && <ChatBox videoId={videoId} />}
          </div>

          {/* Details Section: Tabs and content */}
          {isExpanded ? (
            <div className="flex flex-col h-full w-full">
              {/* Tabs always shown in expanded mode; include Ask AI as a tab */}
              {(
                <TabsSwitch
                  options={[
                    { label: 'Committee Transcript', value: 'transcript' },
                    ...(video.summary ? [{ label: 'Key Points', value: 'key-points' }] : []),
                    ...(video.summary ? [{ label: 'Summary', value: 'summary' }] : []),
                    { label: 'Ask AI', value: 'ask-ai' },
                  ]}
                  isActive={showOption}
                  onSwitch={setShowOption}
                />
              )}

              {showOption === 'transcript' && (
                <TranscriptContainer transcript={video?.transcript} onGoToTimeStamp={onGoToTimeStamp} />
              )}
              {showOption === 'key-points' && video.summary && (
                <CommitteeSummary onTimeSelect={onGoToTimeStamp} summaries={video?.summary} />
              )}
              {showOption === 'summary' && video.summary && (
                <div className="h-full max-h-screen flex-1 flex flex-col py-[15px] px-2.5 sm:px-5 border border-[#E5E7EB] bg-[#F9FAFB]">
                  <div className="border-b border-[#E5E7EB] mb-5 pb-2.5">
                    <h2
                      id="Office-Information"
                      className="font-playfair text-[28px] capitalize text-darkblue font-medium leading-8"
                    >
                      Committee Summary
                    </h2>
                  </div>
                  <div className="flex flex-col gap-5 px-6 py-3 border border-[#E5E7EB] bg-white overflow-y-auto overflow-x-hidden h-[calc(100vh-260px)] whitespace-pre-wrap">
                    {video?.noTimestampSummary}
                  </div>
                </div>
              )}
              {showOption === 'ask-ai' && <ChatBox videoId={videoId} />}
            </div>
          ) : (
            <div className="flex flex-col h-full max-h-screen w-[60%]">
              {video.summary && (
                <TabsSwitch
                  options={[
                    { label: 'Committee Transcript', value: 'transcript' },
                    { label: 'Key Points', value: 'key-points' },
                    { label: 'Summary', value: 'summary' },
                  ]}
                  isActive={showOption}
                  onSwitch={setShowOption}
                />
              )}
              {showOption === 'transcript' && (
                <TranscriptContainer transcript={video?.transcript} onGoToTimeStamp={onGoToTimeStamp} />
              )}
              {showOption === 'key-points' && (
                <CommitteeSummary onTimeSelect={onGoToTimeStamp} summaries={video?.summary} />
              )}
              {showOption === 'summary' && (
                <div className="h-full max-h-screen flex-1 flex flex-col py-[15px] px-2.5 sm:px-5 border border-[#E5E7EB] bg-[#F9FAFB]">
                  <div className="border-b border-[#E5E7EB] mb-5 pb-2.5">
                    <h2
                      id="Office-Information"
                      className="font-playfair text-[28px] capitalize text-darkblue font-medium leading-8"
                    >
                      Committee Summary
                    </h2>
                  </div>
                  <div className="flex flex-col gap-5 px-6 py-3 border border-[#E5E7EB] bg-white overflow-y-auto overflow-x-hidden h-[calc(100vh-260px)] whitespace-pre-wrap">
                    {video?.noTimestampSummary}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Video;
